import { ApolloError } from "@apollo/client";
import {
    RiCalendar2Line, RiCalendarCheckLine, RiCalendarCloseLine, RiEyeLine, RiFileCheckLine,
    RiUser2Fill,
} from "@remixicon/react";
import React, {
    memo, useCallback, useMemo, useState,
} from "react";
import {
    ApprovalRequest, ApprovalRequestRecipient, ApprovalStatus, ListApprovalsQueryResult,
    useGetApprovalDetailsQuery, useListApprovalsQuery,
} from "../../../../../__generated__/api";
import usePortal from "../../../../../contexts/PortalContext";
import { isDateInRange, parseFilterDates, tryParseDate } from "../../../../../date";
import DateRangeCalendarPicker from "../../../../pickers/date-range/DateRangeCalendarPicker";
import Badge from "../../../../theme/badge/Badge";
import Button from "../../../../theme/button/Button";
import ButtonTabs from "../../../../theme/button/ButtonTabs";
import { useFormLoadedDrawer, Size } from "../../../../theme/drawer";
import createTable from "../../../../theme/table";
import Text from "../../../../theme/text/Text";
import Time from "../../../../time/Time";
import UserDisplay from "../../../labels/user/User";
import Styles from "./ApprovalsWidget.module.scss";
import Details from "../../../../theme/details";

const TableContainer = createTable<ApprovalRequest>();

const FEATURE_FLAG_APPROVALS = "frontend.approvals.enabled";

const APPROVAL_STATUS = {
    [ApprovalStatus.Open]: <Badge variant="primary">Pending</Badge>,
    [ApprovalStatus.Approved]: <Badge variant="success">Approved</Badge>,
    [ApprovalStatus.Rejected]: <Badge variant="danger">Rejected</Badge>,
};

interface TitleAction {
    title: string;
    onBack:(() => void) | undefined;
    size: Size;
}

function ApprovalDetails({
    viewApproval,
}: {
    viewApproval: ApprovalRequest | undefined,
}) {
    const { data, loading: isLoading, error } = useGetApprovalDetailsQuery({
        variables: {
            approvalId: viewApproval?.id,
        },
        skip: !viewApproval,
    });

    const details = data?.approval as ApprovalRequest;

    const recipients = useMemo(() => data?.approval?.recipients?.map(recipient => ({
        ...recipient as ApprovalRequestRecipient,
        id: recipient.recipientId,
    })), [data?.approval]);
    return (
        <Details>
            <Details.Section title="General">
                <Details.Row title="Description">
                    {details?.text}
                </Details.Row>
                <Details.Row title="Created Date">
                    <Time date={details?.createdDate} variant="date-time" />
                </Details.Row>
                {details?.requester && (
                    <Details.Row title="Requester">
                        <UserDisplay user={details?.requester} allowOverlay={false} />
                    </Details.Row>
                )}
                <Details.Row title="Status">
                    {APPROVAL_STATUS[details?.status as ApprovalStatus]}
                </Details.Row>

                <Details.Row title="Requesters" className={Styles["requesters-names"]}>
                    {recipients?.map(row => (
                        <div>
                            <UserDisplay user={row?.recipient} allowOverlay={false} />
                        </div>
                    ))}
                </Details.Row>

            </Details.Section>

            {details?.approvedBy && (
                <Details.Section title="Approval">
                    <Details.Row title="Approved By">
                        <UserDisplay user={details?.approvedBy} allowOverlay={false} />
                    </Details.Row>
                    <Details.Row title="Approved Date">
                        <Time date={details?.approvedDate} variant="date-time" />
                    </Details.Row>
                </Details.Section>
            )}

        </Details>
    );
}
function ApprovalsList({
    approvals, isLoading, error, setTitle,
}: {
    approvals: ApprovalRequest[], isLoading: boolean, error: ApolloError | undefined, setTitle: (title: TitleAction) => void,
}) {
    const [selectedStatus, setSelectedStatus] = useState<ApprovalStatus>(ApprovalStatus.Open);
    const approvalData = useMemo(() => approvals?.groupBy(approval => approval.status) ?? {}, [approvals]);

    const [viewApproval, setViewApproval] = useState<ApprovalRequest | undefined>(undefined);

    if (viewApproval) {
        return (
            <ApprovalDetails
                viewApproval={viewApproval}
            />
        );
    }

    return (
        <div>
            <ButtonTabs
                tabVariant="tabs"
                values={[
                    {
                        text: (
                            <>
                                Pending
                                {" "}
                                <Badge variant="counter" isSelected={selectedStatus === ApprovalStatus.Open}>
                                    {approvalData[ApprovalStatus.Open]?.length}
                                </Badge>
                            </>
                        ),
                        key: ApprovalStatus.Open,
                    },
                    {
                        text: (
                            <>
                                Approved
                                {" "}
                                <Badge variant="counter" isSelected={selectedStatus === ApprovalStatus.Approved}>
                                    {approvalData[ApprovalStatus.Approved]?.length}
                                </Badge>
                            </>
                        ),
                        key: ApprovalStatus.Approved,
                    },
                    {
                        text: (
                            <>
                                Rejected
                                {" "}
                                <Badge variant="counter" isSelected={selectedStatus === ApprovalStatus.Rejected}>
                                    {approvalData[ApprovalStatus.Rejected]?.length}
                                </Badge>
                            </>
                        ),
                        key: ApprovalStatus.Rejected,
                    },
                ]}
                onChange={selected => {
                    setSelectedStatus(selected.key as ApprovalStatus);
                }}
                value={selectedStatus}
                className={Styles["approvals-status-tabs"]}
            />

            <div className={Styles["approvals-list"]}>
                <TableContainer data={approvalData[selectedStatus] ?? []} isLoading={isLoading} error={error}>
                    <TableContainer.Empty>
                        <TableContainer.Empty.Default
                            text="Approvals are requests for approval from your team.
                        Once an approval has been sent, you will see it here."
                            title="Approvals"
                            icon={<RiFileCheckLine />}
                        />
                    </TableContainer.Empty>
                    <TableContainer.TableControls>
                        <TableContainer.TableControls.Search
                            search={(query, item) => item.text?.toLocaleLowerCase().includes(query)
                                                    || item.title?.toLocaleLowerCase().includes(query)
                                                    || item.requester?.name?.toLocaleLowerCase().includes(query)
                                                    || item.approvedBy?.name?.toLocaleLowerCase().includes(query)}
                        />
                        <TableContainer.TableControls.Seperator />
                        <TableContainer.TableControls.Filters shouldWriteUrl={false} />

                        <TableContainer.TableControls.Filter
                            defaultValue={{
                                startDate: undefined as Date | undefined,
                                endDate: undefined as Date | undefined,
                            }}
                            deserialize={keys => (typeof keys === "object" && !Array.isArray(keys) ? ({
                                startDate: keys.startDate ? new Date(keys.startDate) : undefined,
                                endDate: keys.endDate ? new Date(keys.endDate) : undefined,
                            }) : undefined)}
                            serialize={value => ({
                                startDate: value.startDate?.toISOString(),
                                endDate: value.endDate?.toISOString(),
                            })}
                            filterKey="createdDate"
                            onFilter={(item, currentValue) => isDateInRange(tryParseDate(item.createdDate), currentValue)}
                        >
                            {(currentValue, setValue) => (
                                <DateRangeCalendarPicker
                                    toggleText="Created"
                                    icon={<RiCalendar2Line />}
                                    value={{
                                        startDate: currentValue.startDate,
                                        endDate: currentValue.endDate,
                                    }}
                                    onChange={change => {
                                        const dates = parseFilterDates(change);
                                        setValue({
                                            startDate: dates.startDate ? new Date(dates.startDate) : undefined,
                                            endDate: dates.endDate ? new Date(dates.endDate) : undefined,
                                        });
                                    }}
                                />
                            )}

                        </TableContainer.TableControls.Filter>
                        {selectedStatus !== ApprovalStatus.Open && (

                            <TableContainer.TableControls.Filter
                                defaultValue={{
                                    startDate: undefined as Date | undefined,
                                    endDate: undefined as Date | undefined,
                                }}
                                deserialize={keys => (typeof keys === "object" && !Array.isArray(keys) ? ({
                                    startDate: keys.startDate ? new Date(keys.startDate) : undefined,
                                    endDate: keys.endDate ? new Date(keys.endDate) : undefined,
                                }) : undefined)}
                                serialize={value => ({
                                    startDate: value.startDate?.toISOString(),
                                    endDate: value.endDate?.toISOString(),
                                })}
                                filterKey="approvedDate"
                                onFilter={(item, currentValue) => isDateInRange(tryParseDate(item.approvedDate), currentValue)}
                            >
                                {(currentValue, setValue) => (
                                    <DateRangeCalendarPicker
                                        toggleText={selectedStatus === ApprovalStatus.Approved ? "Approved" : "Rejected"}
                                        icon={selectedStatus === ApprovalStatus.Approved
                                            ? <RiCalendarCheckLine />
                                            : <RiCalendarCloseLine />}
                                        value={{
                                            startDate: currentValue.startDate,
                                            endDate: currentValue.endDate,
                                        }}
                                        onChange={change => {
                                            const dates = parseFilterDates(change);
                                            setValue({
                                                startDate: dates.startDate ? new Date(dates.startDate) : undefined,
                                                endDate: dates.endDate ? new Date(dates.endDate) : undefined,
                                            });
                                        }}
                                    />
                                )}

                            </TableContainer.TableControls.Filter>
                        )}
                        {/* <TableContainer.TableControls.Filter
                            defaultValue={new Set<string>()}
                            deserialize={TableContainer.TableControls.Filter.Serializers.deserializeSet}
                            serialize={TableContainer.TableControls.Filter.Serializers.serializeSet}
                            filterKey="actorIds"
                            onFilter={(item, selected) => selected.has(item.status)}
                        >
                            {(filter, setStatuses) => (
                                <FiltersSelector
                                    toggleText="Status"
                                    className=""
                                    icon={<RiCheckboxCircleLine />}
                                    categories={CATEGORIES}
                                    searchMinimum={5}
                                    selectedCategories={filter}
                                    onSelected={setStatuses}
                                />
                            )}

                        </TableContainer.TableControls.Filter> */}

                        <TableContainer.TableControls.SortBy />
                    </TableContainer.TableControls>
                    <TableContainer.Table>
                        <TableContainer.TableValue
                            columnKey="title"
                            title="Title"
                            sortBy={item => item.title?.toLowerCase()}
                        >
                            {item => item.title}
                        </TableContainer.TableValue>
                        <TableContainer.TableValue
                            columnKey="text"
                            title="Text"
                            sortBy={item => item.text?.toLowerCase()}
                        >
                            {item => item.text}
                        </TableContainer.TableValue>
                        <TableContainer.TableValue
                            columnKey="requester"
                            title="Requester By"
                            sortBy={item => item.requester?.name?.toLowerCase()}
                        >
                            {item => (item.requester ? <UserDisplay user={item.requester} /> : "")}
                        </TableContainer.TableValue>
                        <TableContainer.TableValue
                            columnKey="createdDate"
                            title="Created"
                            sortBy={item => tryParseDate(item.createdDate)?.getTime()}
                        >
                            {item => (item.createdDate ? <Time date={item.createdDate} variant="date-time" /> : "")}
                        </TableContainer.TableValue>

                        {selectedStatus === ApprovalStatus.Approved && (
                            <>
                                <TableContainer.TableValue
                                    columnKey="approvedBy"
                                    title="Approved By"
                                    sortBy={item => item.approvedBy?.name?.toLowerCase()}
                                >
                                    {item => (item.approvedBy ? <UserDisplay user={item.approvedBy} /> : "")}
                                </TableContainer.TableValue>
                                <TableContainer.TableValue
                                    columnKey="approvedAt"
                                    title="Approved Date"
                                    sortBy={item => tryParseDate(item.approvedDate)?.getTime()}
                                >
                                    {item => (item.approvedDate ? <Time date={item.approvedDate} variant="date-time" /> : "")}
                                </TableContainer.TableValue>
                            </>
                        )}
                        {selectedStatus === ApprovalStatus.Rejected && (
                            <>
                                <TableContainer.TableValue
                                    columnKey="rejectedBy"
                                    title="Rejected By"
                                    sortBy={item => item.approvedBy?.name?.toLowerCase()}
                                >
                                    {item => (item.approvedBy ? <UserDisplay user={item.approvedBy} /> : "")}
                                </TableContainer.TableValue>
                                <TableContainer.TableValue
                                    columnKey="rejectedAt"
                                    title="Rejected Date"
                                    sortBy={item => tryParseDate(item.approvedDate)?.getTime()}
                                >
                                    {item => (item.approvedDate ? <Time date={item.approvedDate} variant="date-time" /> : "")}
                                </TableContainer.TableValue>
                            </>
                        )}
                        <TableContainer.Action
                            icon={<RiEyeLine />}
                            altText="View Approval"
                            onClick={item => {
                                setViewApproval(item);
                                setTitle({
                                    title: item.title ?? "",
                                    size: "medium",
                                    onBack: () => {
                                        setViewApproval(undefined);
                                        setTitle({
                                            title: "Approvals",
                                            onBack: undefined,
                                            size: "xlarge",
                                        });
                                    },
                                });
                            }}
                        />

                    </TableContainer.Table>
                </TableContainer>

            </div>
        </div>
    );
}

const DEFAULT_DRAWER: TitleAction = {
    title: "Approvals",
    onBack: undefined,
    size: "xlarge",
};

function ApprovalsWidget({ metricPathPrefix }: { metricPathPrefix: string[] }) {
    const { featureFlags } = usePortal();

    const [title, setTitle] = useState<TitleAction>(DEFAULT_DRAWER);
    const bodyCreator = useCallback(
        (data: ListApprovalsQueryResult) => (
            <ApprovalsList
                approvals={data.data?.approvals as ApprovalRequest[]}
                isLoading={data.loading}
                error={data.error}
                setTitle={setTitle}
            />
        ),
        [],
    );

    const { element, setOpen } = useFormLoadedDrawer({
        query: useListApprovalsQuery,
        placement: "end",
        queryOptions: {},
        dependencies: [],
        title: (
            <div className={Styles["approval-title"]}>
                {title.onBack ? <Button.Back onClick={title.onBack} /> : null}
                <Text textVariant="subtitle-1" as="h2">{title.title}</Text>
            </div>
        ),
        className: Styles["approvals-drawer"],
        bodyCreator,
        size: title.size,
        shouldHandleLoading: true,
    });

    return featureFlags.has(FEATURE_FLAG_APPROVALS) ? (
        <>
            {element}
            <Button
                size="small"
                variant="ghost-gray"
                prefixIcon={<RiFileCheckLine />}
                metricPath={[...metricPathPrefix, "approvals"]}
                onClick={() => {
                    setOpen(true);
                    setTitle(DEFAULT_DRAWER);
                }}
            />
        </>
    ) : null;
}

export default memo(ApprovalsWidget);
